import {clearStoredApiClient} from '@/api/utils';
import {API_URL} from '@/utils/constants';
import {Log} from '@/utils/log';
import {removeKeys} from '@/utils/removeKeys';
import axios, {
    AxiosError,
    AxiosInstance,
    InternalAxiosRequestConfig,
} from 'axios';
import {stringify} from 'query-string';
import {GetListParams, GetListResult, HttpError, Identifier} from 'react-admin';
import {NavigateFunction} from 'react-router-dom';

type AuthTokens = {
    id_token: string;
    access_token: string;
    refresh_token: string;
    user_id: string;
};

type APIPaginationResponse = {
    currentPage: number;
    nextPage: number | null;
    prevPage: number | null;
    total: number;
};

type PendingRetry = {
    resolve: (value: unknown) => void;
    reject: (reason?: unknown) => void;
    config: InternalAxiosRequestConfig;
};

export class API {
    private client: AxiosInstance;
    private tokens: AuthTokens;
    private isRefreshing = false;
    private pendingRequests: PendingRetry[] = [];

    constructor(tokens: AuthTokens, navigate: NavigateFunction) {
        this.tokens = tokens;

        this.client = axios.create({
            baseURL: API_URL,
            headers: {Authorization: tokens.id_token},
        });

        // Immediately set the default Authorization header
        // in case axios internal handling skips initial headers
        this.client.defaults.headers.Authorization = tokens.id_token;

        this.client.interceptors.response.use(
            (response) => response,
            /**
             * Handle rejected responses
             */
            async (error: AxiosError) => {
                const response = error.response;

                if (!response) throw error;

                /**
                 * Refresh the token on any 401 response
                 */
                if (response.status === 401 && this.tokens) {
                    return this.handleUnauthorizedError(error, navigate);
                }

                Log.error(error);

                throw error;
            }
        );
    }

    /**
     * Handle refreshing the token on any unauthorized errors by queuing
     * pending requests and handling refresh logic.
     */
    private handleUnauthorizedError = async (
        error: AxiosError,
        navigate: NavigateFunction
    ) => {
        Log.info('handleUnauthorizedError...');

        const originalRequest = error.config;

        if (!originalRequest) {
            return this.endSessionAndThrow(
                navigate,
                new HttpError('Authentication request failed', 401)
            );
        }

        if (this.isRefreshing) {
            return new Promise((resolve, reject) => {
                this.pendingRequests.push({
                    resolve,
                    reject,
                    config: originalRequest,
                });
            });
        }

        this.isRefreshing = true;

        try {
            Log.info('Refreshing token...');

            const user_id = this.tokens.user_id;

            const tokenResponse = await this.refreshToken();

            this.tokens = {...tokenResponse, user_id};
            localStorage.setItem('auth', JSON.stringify(this.tokens));

            this.client.defaults.headers.Authorization = this.tokens.id_token;

            if (originalRequest.headers) {
                originalRequest.headers.Authorization = this.tokens.id_token;
            }

            const queued = [...this.pendingRequests];
            this.pendingRequests = [];
            await Promise.all(
                queued.map(({config, resolve, reject}) =>
                    this.client(config).then(resolve).catch(reject)
                )
            );

            return this.client(originalRequest);
        } catch (refreshError) {
            Log.error(refreshError);

            return this.endSessionAndThrow(
                navigate,
                new HttpError(
                    'Your session has expired. Please sign in again.',
                    401
                )
            );
        } finally {
            this.isRefreshing = false;
        }
    };

    /**
     * Reject all waiters, clear storage and cached API client, then go to login.
     */
    private endSessionAndThrow = (
        navigate: NavigateFunction,
        sessionError: HttpError
    ): never => {
        const queued = [...this.pendingRequests];
        this.pendingRequests = [];
        for (const {reject} of queued) {
            reject(sessionError);
        }

        localStorage.removeItem('auth');
        clearStoredApiClient();

        Log.breadcrumb('Admin session ended', {
            reason: 'token_refresh_failed',
        });

        navigate('/login');

        throw sessionError;
    };

    async getList(resource: string, params: GetListParams) {
        const queryParams = {
            ...params.filter,
            page: params.pagination?.page,
            page_size: params.pagination?.perPage,
        };

        /**
         * Sorting is currently only supported for 'users', and 'dogs' for the
         * 'user_id' field. So only add sort params in this instance.
         */
        if (resource === 'users' || (resource === 'dogs' && params?.sort?.field === 'user_id')) {
            queryParams.sort = params.sort?.field;
            queryParams.sort_dir = params.sort?.order.toLowerCase();
        }

        const getPageInfo = (pagination: APIPaginationResponse) => {
            const {nextPage, prevPage, total} = pagination;

            const pageInfo = {
                hasNextPage: typeof nextPage === 'number',
                hasPreviousPage: typeof prevPage === 'number',
            };

            return {total, pageInfo};
        };

        switch (resource) {
            case 'breeds': {
                const response = await this.client.get(`/dogs/breeds`);
                const breeds = response.data.data.breeds as Array<{
                    id: string;
                    label: string;
                }>;

                // Sort alphabetically by breed name
                const data = [...breeds].sort((a, b) =>
                    a.label.localeCompare(b.label)
                );

                return {data, total: data.length} as GetListResult;
            }
            case 'dogs': {
                const response = await this.client.get(
                    `/admin/dogs?${stringify(queryParams)}`
                );

                const data = response.data.data.dogs;
                return {data, ...getPageInfo(response.data.data.pagination)};
            }
            case 'licenses': {
                const response = await this.client.get(
                    `/admin/licenses?${stringify(queryParams)}`
                );
                const data = response.data.data.licenses;
                return {data, ...getPageInfo(response.data.data.pagination)};
            }
            case 'rules': {
                const response = await this.client.get(
                    `/admin/breed_rules?${stringify(queryParams)}`
                );
                const data = response.data.data.rules;
                return {data, ...getPageInfo(response.data.data.pagination)};
            }
            case 'users': {
                const response = await this.client.get(
                    `/admin/users?${stringify(queryParams)}`
                );
                const data = response.data.data.users;
                return {data, ...getPageInfo(response.data.data.pagination)};
            }
            default: {
                throw new Error(
                    `Unsupported API.getList() resource: ${resource}`
                );
            }
        }
    }

    async getOne(resource: string, id: Identifier) {
        switch (resource) {
            case 'breeds': {
                /**
                 * No API endpoint to fetch a single breed, so filtering
                 * from the whole list
                 */
                const response = await this.client.get(`/dogs/breeds`);
                const breeds = response.data.data.breeds as Array<{
                    id: string;
                    label: string;
                }>;

                return breeds.find((breed) => breed.id === id);
            }
            case 'dogs': {
                try {
                    const response = await this.client.get(`/admin/dog/${id}`);
                    return response.data.data.dog;
                } catch (error) {
                    // Handle 404 errors gracefully for deleted records
                    if (error instanceof AxiosError && error.response?.status === 404) {
                        return null;
                    }
                    throw error;
                }
            }
            case 'licenses': {
                try {
                    const response = await this.client.get(`/admin/license/${id}`);
                    return response.data.data.license;
                } catch (error) {
                    // Handle 404 errors gracefully for deleted records
                    if (error instanceof AxiosError && error.response?.status === 404) {
                        return null;
                    }
                    throw error;
                }
            }
            case 'rules': {
                try {
                    const response = await this.client.get(
                        `/admin/breed_rules/${id}`
                    );
                    return {id, breed_ids: response.data.data.breed_ids};
                } catch (error) {
                    // Handle 404 errors gracefully for deleted records
                    if (error instanceof AxiosError && error.response?.status === 404) {
                        return null;
                    }
                    throw error;
                }
            }
            case 'users': {
                try {
                    const response = await this.client.get(`/admin/user/${id}`);
                    return response.data.data.user;
                } catch (error) {
                    // Handle 404 errors gracefully for deleted records
                    if (error instanceof AxiosError && error.response?.status === 404) {
                        return null;
                    }
                    throw error;
                }
            }
            default: {
                throw new Error(
                    `Unsupported API.getOne() resource: ${resource}`
                );
            }
        }
    }

    async create(resource: string, data: Record<string, unknown>) {
        switch (resource) {
            case 'rules': {
                await this.client.put(`/admin/breed_rules/${data.breed_id}`, {
                    breed_ids: data.breed_ids,
                });

                return {id: data.breed_id, breed_ids: data.breed_ids};
            }
            default: {
                throw new Error(
                    `Unsupported API.create() resource: ${resource}`
                );
            }
        }
    }

    async update(resource: string, id: string, data: Record<string, unknown>) {
        switch (resource) {
            case 'dogs': {
                /**
                 * react-admin passes through some data that we don't
                 * want to update
                 */
                const filteredData = removeKeys(data, [
                    'images',
                    'profile_image',
                    'user',
                ]);

                const response = await this.client.put(
                    `/admin/dog/${id}`,
                    filteredData
                );
                return response.data.data.dog;
            }
            case 'licenses': {
                const response = await this.client.put(
                    `/admin/license/${id}`,
                    data
                );
                return response.data.data.license;
            }
            default: {
                throw new Error(
                    `Unsupported API.update() resource: ${resource}`
                );
            }
        }
    }

    async delete(resource: string, id: Identifier) {
        switch (resource) {
            case 'rules': {
                const response = await this.client.delete(
                    `/admin/breed_rule/${id}`
                );
                return response.data.data;
            }
            default: {
                throw new Error(
                    `Unsupported API.delete() resource: ${resource}`
                );
            }
        }
    }

    async setUserPassword(userId: string, password: string) {
        const response = await this.client.put(
            `/admin/user/${userId}/password`,
            {password}
        );
        return response.data;
    }

    async setUserAdminStatus(userId: string, isAdmin: boolean) {
        const response = await this.client.put(
            `/admin/user/${userId}/admin`,
            {is_admin: isAdmin}
        );
        return response.data;
    }

    async checkAuth() {
        const response = await this.client.get(`/user`);
        return !!response.data.success;
    }

    async refreshToken(): Promise<AuthTokens> {
        /**
         * Use a plain axios call so a 401 from `/user/refresh` never hits
         * this.client's response interceptor (which would queue while
         * isRefreshing is still true and deadlock refresh).
         */
        const response = await axios.post(`${API_URL}/user/refresh`, {
            user_id: this.tokens.user_id,
            refresh_token: this.tokens.refresh_token,
        });

        return response.data.data.auth;
    }

    async logout() {
        const response = await this.client.post(
            `/user/logout`,
            {},
            {
                headers: {'Refresh-Token': this.tokens.refresh_token},
            }
        );

        return !!response.data.success;
    }
}
